import React from 'react';
import { useSelector } from 'react-redux';
import {
  authenticatedUser,
  isLoading,
  signInServiceName,
  isVerifiedUser,
} from 'platform/user/authentication/selectors';
import {
  VerifyIdmeButton,
  VerifyLogingovButton,
  VerifyClearButton,
} from 'platform/user/authentication/components/VerifyButton';
import {
  CSP_IDS,
  SERVICE_PROVIDERS,
} from 'platform/user/authentication/constants';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';

const VERIFY_CONTENT = {
  [CSP_IDS.ID_ME]: {
    label: SERVICE_PROVIDERS[CSP_IDS.ID_ME].label,
    button: VerifyIdmeButton,
  },
  [CSP_IDS.LOGIN_GOV]: {
    label: SERVICE_PROVIDERS[CSP_IDS.LOGIN_GOV].label,
    button: VerifyLogingovButton,
  },
  [CSP_IDS.CLEAR]: {
    label: SERVICE_PROVIDERS[CSP_IDS.CLEAR].label,
    button: VerifyClearButton,
  },
};

export default function Verify() {
  const isAuthenticated = useSelector(authenticatedUser);
  const isVerified = useSelector(isVerifiedUser);
  const loading = useSelector(isLoading);
  const loginServiceName = useSelector(signInServiceName);
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const clearSigninEnabled = useToggleValue(TOGGLE_NAMES.clearSigninEnabled);

  let renderServiceNames;
  if (!loading && isAuthenticated) {
    renderServiceNames = (
      <strong>
        {VERIFY_CONTENT[loginServiceName]?.label ||
          VERIFY_CONTENT[CSP_IDS.LOGIN_GOV].label}
      </strong>
    );
  } else {
    renderServiceNames = clearSigninEnabled ? (
      <>
        <strong>ID.me</strong>, <strong>Login.gov</strong>, or{' '}
        <strong>
          <span aria-label="Clear">CLEAR</span>
        </strong>
      </>
    ) : (
      <>
        <strong>ID.me</strong> or <strong>Login.gov</strong>
      </>
    );
  }

  // Handle button content based on authentication and login service
  let buttonContent;
  if (!loading && isAuthenticated) {
    const VerifyButton =
      VERIFY_CONTENT[loginServiceName]?.button ||
      VERIFY_CONTENT[CSP_IDS.LOGIN_GOV].button;
    buttonContent = (
      <VerifyButton queryParams={{ operation: 'verify_page_authenticated' }} />
    );
  } else {
    buttonContent = (
      <>
        <VerifyIdmeButton
          useOAuth
          queryParams={{ operation: 'verify_page_unauthenticated' }}
        />
        <VerifyLogingovButton
          useOAuth
          queryParams={{ operation: 'verify_page_unauthenticated' }}
        />
        {clearSigninEnabled && (
          <VerifyClearButton
            useOAuth
            queryParams={{ operation: 'verify_page_unauthenticated' }}
          />
        )}
      </>
    );
  }

  return (
    <section data-testid="unauthenticated-verify-app" className="verify">
      <div className="container">
        <div className="row">
          <div className="columns small-12 fed-warning--v2 vads-u-margin-y--2">
            <h1 className="vads-u-margin-top--2">Verify your identity</h1>

            {/* If authenticated and verified */}
            {isAuthenticated && isVerified && (
              <>
                <va-alert status="success">
                  <h2>You’re verified</h2>
                  <p>
                    We confirmed you’ve already completed the identity
                    verification process for VA online services.
                  </p>
                  <p>
                    This one-time step helps protect your information and
                    prevents scammers from stealing your benefits. You can now
                    access your VA benefits, services, and information online.
                  </p>
                </va-alert>
                <p>
                  <a href="/my-va">Go to My VA</a>
                </p>
              </>
            )}

            {!isVerified && !loading && (
              <>
                <p>
                  We need you to verify your identity for your{' '}
                  {renderServiceNames} account. This step helps us protect all
                  Veterans’ information and prevent scammers from stealing your
                  benefits.
                </p>
                <p>
                  This one-time process often takes about 10 minutes. You’ll
                  need to provide certain personal information and
                  identification.
                </p>
                <div data-testid="verify-button-group">{buttonContent}</div>
                <a href="/resources/verifying-your-identity-on-vagov/">
                  Learn more about verifying your identity
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
